import { templatePlaceholders, type RecordEntity } from '@crm/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Editor } from '@tiptap/react';
import { ChevronDown } from 'lucide-react';
import { useCallback, useState, type ReactNode } from 'react';
import { describeError } from '../../lib/api.ts';
import { api, type NoteTemplateView } from '../../lib/client.ts';
import {
  CONTEXT_GROUP,
  fetchRecordValues,
  fillNoteHtml,
  kindGroup,
  mergeValues,
  noteBundle,
  recordName,
  type MergeFieldGroup,
} from '../../lib/merge-fields.ts';
import { useSession } from '../../lib/session.tsx';
import { cn } from '../../lib/utils.ts';
import { Input } from '../ui/input.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover.tsx';
import { RichTextEditor } from '../ui/rich-text.tsx';
import { NOTE_SOURCE_KIND } from './note-origin-filter.tsx';

const triggerClass =
  'border-input text-muted-foreground hover:text-foreground inline-flex h-6 items-center gap-1 rounded-md border px-2 text-xs';

/** A folded section drawn like the sidebar's groups: small caps label and a chevron. */
function Fold({
  label,
  open,
  onToggle,
  children,
  nested,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
  nested?: boolean;
}) {
  return (
    <div className={cn('flex flex-col', nested && 'pl-2')}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="text-muted-foreground hover:text-foreground flex items-center justify-between rounded-md py-1 text-[10px] font-semibold tracking-wider uppercase"
      >
        {label}
        <ChevronDown
          className={cn('size-3 transition-transform', !open && '-rotate-90')}
          aria-hidden
        />
      </button>
      {open && <div className="flex flex-col">{children}</div>}
    </div>
  );
}

function FieldRow({ label, onPick }: { label: string; onPick: () => void }) {
  return (
    <button
      type="button"
      onClick={onPick}
      className="hover:bg-muted rounded-md px-2 py-1 text-left text-sm"
    >
      {label}
    </button>
  );
}

/**
 * "Insert field": the merge fields in folds, one per group, the record kind's custom fields in a
 * "Custom" fold inside its own. A search shows every match, folded or not.
 */
export function MergeFieldPicker({
  groups,
  onPick,
}: {
  groups: readonly MergeFieldGroup[];
  onPick: (path: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [unfolded, setUnfolded] = useState<ReadonlySet<string>>(new Set([CONTEXT_GROUP.label]));
  const needle = search.trim().toLowerCase();
  const match = (f: { label: string }) => f.label.toLowerCase().includes(needle);
  const isOpen = (key: string) => needle !== '' || unfolded.has(key);
  const toggle = (key: string) =>
    setUnfolded((s) => {
      const next = new Set(s);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  const pick = (path: string) => {
    setOpen(false);
    setSearch('');
    onPick(path);
  };
  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger className={triggerClass} onMouseDown={(e) => e.preventDefault()}>
        Insert field <ChevronDown className="size-3" />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="flex max-h-80 w-64 flex-col gap-1 overflow-y-auto p-1.5"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <Input
          aria-label="Search fields"
          placeholder="Search fields"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-7 text-xs"
        />
        {groups.map((g) => {
          const fields = g.fields.filter(match);
          const custom = (g.custom ?? []).filter(match);
          if (fields.length === 0 && custom.length === 0) return null;
          const customKey = `${g.label}/custom`;
          return (
            <Fold
              key={g.label}
              label={g.label}
              open={isOpen(g.label)}
              onToggle={() => toggle(g.label)}
            >
              {fields.map((f) => (
                <FieldRow key={f.path} label={f.label} onPick={() => pick(f.path)} />
              ))}
              {custom.length > 0 && (
                <Fold
                  label="Custom"
                  nested
                  open={isOpen(customKey)}
                  onToggle={() => toggle(customKey)}
                >
                  {custom.map((f) => (
                    <FieldRow key={f.path} label={f.label} onPick={() => pick(f.path)} />
                  ))}
                </Fold>
              )}
            </Fold>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

/** The merge fields of one record kind (with its custom fields), after the context ones. */
export function useMergeFieldGroups(kind: RecordEntity | null): MergeFieldGroup[] {
  const session = useSession();
  const defs = useQuery({
    queryKey: ['field-definitions', kind],
    queryFn: () => api.fieldDefinitions.list(kind!),
    enabled: kind !== null && session.isEnabled('custom_fields.enabled'),
  });
  if (kind === null) return [CONTEXT_GROUP];
  return [CONTEXT_GROUP, kindGroup(kind, NOTE_SOURCE_KIND[kind], defs.data ?? [])];
}

/** "Insert template": the templates of the record's kind first, then the global ones. */
function TemplatePicker({
  entityType,
  onPick,
}: {
  entityType: RecordEntity;
  onPick: (t: NoteTemplateView) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const templates = useQuery({
    queryKey: ['note-templates', entityType],
    queryFn: () => api.noteTemplates.list(entityType),
    enabled: open,
  });
  const needle = search.trim().toLowerCase();
  const shown = (templates.data ?? []).filter((t) => t.name.toLowerCase().includes(needle));
  const sections = [
    { label: NOTE_SOURCE_KIND[entityType], items: shown.filter((t) => !t.global) },
    { label: 'Global', items: shown.filter((t) => t.global) },
  ];
  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger className={triggerClass} onMouseDown={(e) => e.preventDefault()}>
        Insert template <ChevronDown className="size-3" />
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="flex max-h-80 w-64 flex-col gap-1 overflow-y-auto p-1.5"
        onCloseAutoFocus={(e) => e.preventDefault()}
        data-testid="template-picker"
      >
        <Input
          aria-label="Search templates"
          placeholder="Search templates"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-7 text-xs"
        />
        {templates.isPending && <p className="text-muted-foreground px-2 py-1 text-xs">Loading…</p>}
        {templates.error && (
          <p className="text-danger px-2 py-1 text-xs" role="alert">
            {describeError(templates.error)}
          </p>
        )}
        {templates.data && shown.length === 0 && (
          <p className="text-muted-foreground px-2 py-1 text-xs">No templates</p>
        )}
        {sections.map(
          (s) =>
            s.items.length > 0 && (
              <div key={s.label} className="flex flex-col">
                <span className="text-muted-foreground px-1 py-1 text-[10px] font-semibold tracking-wider uppercase">
                  {s.label}
                </span>
                {s.items.map((t) => (
                  <FieldRow
                    key={t.id}
                    label={t.name}
                    onPick={() => {
                      setOpen(false);
                      setSearch('');
                      onPick(t);
                    }}
                  />
                ))}
              </div>
            ),
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * The rich text editor for a note on a record, with "Insert template" and "Insert
 * field" when `notes.templates` is on. Both insert with every merge field already filled from
 * the record, so the note keeps values, never placeholders. Once a template with "pin by default"
 * is inserted, `onTemplatePinned` ticks a new note's Pin box (never unticks it; an edit ignores it).
 */
export function NoteEditor({
  target,
  value,
  onChange,
  label,
  autoFocus,
  className,
  onSubmit,
  onTemplatePinned,
}: {
  target: { entityType: RecordEntity; entityId: string };
  value: string;
  onChange: (html: string) => void;
  label: string;
  autoFocus?: boolean;
  className?: string;
  onSubmit?: () => void;
  onTemplatePinned?: (pinned: boolean) => void;
}) {
  const session = useSession();
  const qc = useQueryClient();
  const [editor, setEditor] = useState<Editor | null>(null);
  const [error, setError] = useState<string | null>(null);
  const onEditor = useCallback((e: Editor | null) => setEditor(e), []);
  const templates = session.isEnabled('notes.templates');
  const groups = useMergeFieldGroups(templates ? target.entityType : null);
  /** A template goes in as blocks of its own (after the cursor's block); a field at the cursor. */
  const insertFilled = async (html: string, asBlocks: boolean): Promise<boolean> => {
    setError(null);
    try {
      const read = (kind: RecordEntity, id: string) =>
        qc.fetchQuery({
          queryKey: ['merge-record', kind, id],
          queryFn: () => fetchRecordValues(kind, id),
          staleTime: 30_000,
        });
      const [record, definitions, members] = await Promise.all([
        read(target.entityType, target.entityId),
        session.isEnabled('custom_fields.enabled')
          ? qc.fetchQuery({
              queryKey: ['field-definitions', target.entityType],
              queryFn: () => api.fieldDefinitions.list(target.entityType),
            })
          : [],
        qc.fetchQuery({
          queryKey: ['users', session.me.organisation.id],
          queryFn: () => api.users(),
          staleTime: 60_000,
        }),
      ]);
      const values = await mergeValues(
        target.entityType,
        record,
        {
          definitions,
          userName: (id) => members.find((m) => m.id === id)?.name,
          recordName: async (kind, id) => recordName(kind, await read(kind, id)),
        },
        templatePlaceholders(html),
      );
      const bundle = noteBundle(target.entityType, record, values, {
        name: session.me.user.name,
      });
      if (!editor) return false;
      const filled = fillNoteHtml(html, bundle);
      const { $to } = editor.state.selection;
      if (!asBlocks || editor.isEmpty || $to.depth === 0)
        editor.chain().focus().insertContent(filled).run();
      else editor.chain().focus().insertContentAt($to.after(1), filled).run();
      return true;
    } catch (err) {
      setError(describeError(err));
      return false;
    }
  };
  return (
    <div className="flex flex-col gap-1">
      <RichTextEditor
        value={value}
        onChange={onChange}
        label={label}
        autoFocus={autoFocus}
        className={className}
        onEditor={onEditor}
        onSubmit={onSubmit}
        toolbar={
          templates && (
            <>
              <TemplatePicker
                entityType={target.entityType}
                onPick={(t) => {
                  void insertFilled(t.body, true).then((inserted) => {
                    if (inserted && t.pinnedByDefault) onTemplatePinned?.(true);
                  });
                }}
              />
              <MergeFieldPicker
                groups={groups}
                onPick={(path) => void insertFilled(`{{${path}}}`, false)}
              />
            </>
          )
        }
      />
      {error && (
        <p className="text-danger text-xs" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
